/**
 * OrderSummary - 주문 요약 컴포넌트
 *
 * PaymentModal에서 분리된 주문 요약 UI
 * - 소계(세전가), 세금, 팁 표시
 * - 포인트 할인 표시
 * - 총합 표시
 * - 적립 예정 포인트 표시
 *
 * @note 백엔드 API는 메뉴 가격을 세전가(net price)로 내려줍니다.
 * @note 따라서 순가격(net price)은 소계(subtotal)과 동일합니다.
 */

import { Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/AppContext';
import { useTaxRate } from '@/contexts/StorePricingContext';

interface OrderSummaryProps {
  formatCurrency: (amount: number) => string;
  calculateSubtotal: () => number;
  calculateTax: () => number;
  calculateTipAmount: () => number;
  calculateFinalTotal: () => number;
  selectedTip: number;
  pointsToUse: number;
  estimatedPoints?: number;
  isEstimatingPoints?: boolean;
  t: {
    orderSummary: string;
    subtotal: string;
    netPrice: string;
    tax: string;
    tip: string;
    total: string;
    pointsDiscount: string;
    pointsDiscountApplied: string;
    estimatedPoints: string;
    estimating: string;
  };
}

export function OrderSummary({
  formatCurrency,
  calculateSubtotal,
  calculateTax,
  calculateTipAmount,
  calculateFinalTotal,
  selectedTip,
  pointsToUse,
  estimatedPoints,
  isEstimatingPoints,
  t,
}: OrderSummaryProps) {
  const { language } = useLanguage();
  const { taxRatePercent } = useTaxRate();

  return (
    <div className="bg-muted rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-6">
      <h3 className="font-medium mb-3 sm:mb-4 text-sm sm:text-base text-foreground">
        {t.orderSummary}
      </h3>
      <div className="space-y-2 text-xs sm:text-sm">
        {/* 소계 (세전가) */}
        <div className="flex justify-between items-center">
          <span className="text-muted-foreground">{t.subtotal}</span>
          <span className="font-medium text-foreground">
            {formatCurrency(calculateSubtotal())}
          </span>
        </div>

        {/* 세금 (16% IVA) */}
        <div className="flex justify-between items-center text-[10px] sm:text-xs">
          <span className="text-muted-foreground ml-3 sm:ml-4">
            └ {t.tax} ({taxRatePercent}% IVA)
          </span>
          <span className="text-muted-foreground">
            {formatCurrency(calculateTax())}
          </span>
        </div>

        {/* 팁 */}
        <div className="flex justify-between items-center">
          <div className="flex flex-col">
            <span className="text-muted-foreground">
              {t.tip} {selectedTip > 0 ? `${selectedTip}%` : ''}
            </span>
            {pointsToUse > 0 && (
              <span className="text-xs text-muted-foreground">
                {language === 'ko'
                  ? '포인트 차감 전 금액 기준'
                  : language === 'es'
                    ? 'Basado en monto antes de puntos'
                    : 'Based on amount before points'}
              </span>
            )}
          </div>
          <div className="text-right">
            <div className="font-medium text-foreground">
              {formatCurrency(calculateTipAmount())}
            </div>
          </div>
        </div>

        {/* 포인트 할인 표시 - 포인트 적용 시에만 표시 */}
        {pointsToUse > 0 && (
          <div className="flex justify-between items-center text-sm">
            <span className="text-foreground">{t.pointsDiscount}</span>
            <span className="text-secondary font-medium">
              -{formatCurrency(pointsToUse)}
            </span>
          </div>
        )}

        {/* 총합 - 최종 결제 금액 */}
        <div className="border-t border-border pt-3 mt-3">
          <div className="flex justify-between items-center">
            <span className="text-foreground font-bold text-base sm:text-lg">
              {t.total}
            </span>
            <span className="text-primary font-bold text-lg sm:text-xl">
              {formatCurrency(calculateFinalTotal())}
            </span>
          </div>
          {pointsToUse > 0 && (
            <p className="text-[10px] sm:text-xs text-muted-foreground mt-1 text-right">
              {t.pointsDiscountApplied}
            </p>
          )}

          {(isEstimatingPoints || estimatedPoints != null) && (
            <div className="flex justify-between items-center mt-2 pt-2 border-t border-dashed border-border">
              <span className="text-muted-foreground text-xs sm:text-sm">
                {t.estimatedPoints}
              </span>
              {isEstimatingPoints ? (
                <span className="flex items-center gap-1 text-muted-foreground text-xs sm:text-sm">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {t.estimating}
                </span>
              ) : (
                <span className="text-secondary font-bold text-sm sm:text-base">
                  +{estimatedPoints?.toLocaleString()}P
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
